/*
 * Venue-local date and time formatting (build rule 7, invariant 6).
 *
 * THE RULE, in full: "Venue-local times always; zone abbreviation only when it
 * differs from the device zone. Dates: weekday+day, month added across month
 * spans."
 *
 * WHY THE SERVER CANNOT DO ANY OF THIS. It sends a UTC instant plus the venue's
 * IANA zone and stops (contract §0). Everything below needs the DEVICE zone too
 * — which the server does not know — and the whole point of the zone-abbreviation
 * rule is a comparison between the two. A coach at a tournament in their own city
 * should never see "CDT" appended to every time; a coach watching their fencer
 * compete three time zones away must see it on every one.
 *
 * ALL FORMATTING GOES THROUGH Intl, never a hand-rolled table. That is what makes
 * the tier-1 locales (§7: en, zh, es, fr, ru, sr, ar) work without seven
 * codepaths, and it is why month and weekday names never appear as literals in
 * this file.
 *
 * BUT NOT THE VENUE'S OFFSET (decision 785). Intl reads a `timeZone` through the
 * browser's own tz database, and Firefox's had Casablanca an hour wrong in
 * October 2026. Where the server has stated the venue zone's offset
 * (lib/zones.ts), an instant is shifted by it and formatted AS UTC, which needs
 * no zone data; Intl still supplies every word and digit. A zone the server did
 * not state is formatted through the browser, as before.
 */

import { browserOffset, gmtLabel, shifted, venueClock, type VenueClock, type ZoneSpan } from "./zones";

/** The device's own zone, as the browser reports it. */
export function deviceZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The venue zone's short name at a given instant — "CDT", "GMT+8".
 *
 * At an INSTANT rather than for the zone, because the abbreviation is not a
 * property of the zone: America/Chicago is CST in January and CDT in July, and a
 * tournament straddling a DST change would otherwise be labelled wrongly on one
 * of its days.
 */
function zoneAbbrev(instant: Date, timeZone: string, locale: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: "short",
  }).formatToParts(instant);
  return withoutZeroOffset(parts.find((p) => p.type === "timeZoneName")?.value ?? "");
}

/**
 * Intl's name for a zone at offset zero, without the zero: "GMT+0" is "GMT",
 * "UTC+0" is "UTC" (decision 788: `GMT` for zero, never `GMT+0`).
 *
 * Intl spells zero both ways, by zone and by locale: English reads London as
 * "GMT" and Casablanca as "GMT+0" once the runtime knows Morocco is at +00, and
 * German and Japanese read London as "GMT+0". Every other name passes through.
 */
export function withoutZeroOffset(name: string): string {
  return name.replace(/^(GMT|UTC)[+\-−]0{1,2}(:00)?$/, "$1");
}

/**
 * Whether the venue zone needs naming.
 *
 * Compared by ABBREVIATION at the instant, not by IANA name. America/New_York
 * and America/Toronto are different zones that are both EDT all summer: printing
 * "EDT" to a user already in it adds a word and no information, which is what the
 * rule is guarding against. Two zones that agree on the offset today but differ
 * in name are, for the purpose of reading a start time, the same zone.
 */
export function zoneDiffersFromDevice(
  instant: Date,
  timeZone: string,
  locale = dateLocale(),
  device = deviceZone(),
): boolean {
  if (timeZone === device) return false;
  // With the server's offset, the comparison is by OFFSET: the same reading of
  // a start time, which is what the abbreviation comparison stood for.
  const clock = venueClock(timeZone, instant);
  if (clock) return clock.offset !== browserOffset(device, instant);
  return zoneAbbrev(instant, timeZone, locale) !== zoneAbbrev(instant, device, locale);
}

/**
 * The venue zone's name beside a time: the browser's own ("CDT") where the
 * browser's data agrees with the server's offset, else Intl's spelling of the
 * fixed offset ("GMT") -- which is the stale-browser case, where the browser's
 * name for the zone would carry its wrong offset with it.
 */
function venueAbbrev(instant: Date, timeZone: string, clock: VenueClock, locale: string): string {
  if (browserOffset(timeZone, instant) === clock.offset) return zoneAbbrev(instant, timeZone, locale);
  return gmtLabel(clock.offset);
}

export interface TimeOptions {
  /** The venue's IANA zone. Absent means the device zone, which is all we have. */
  timeZone?: string | null;
  locale?: string;
  /** Injectable for tests, so the device zone is not the machine's. */
  device?: string;
  /**
   * The venue zone's offsets, where the caller carries its own rather than
   * `/api/me`'s table: the service worker, from the push payload (decision 785).
   */
  spans?: readonly ZoneSpan[] | null;
}

/**
 * A venue-local clock time: "2:40 PM", or "2:40 PM CDT" when the zone differs.
 *
 * Returns null for a null instant rather than a placeholder, because "show what
 * exists" means the caller renders nothing at all (contract §0).
 */
export function venueTime(
  instant: string | null | undefined,
  { timeZone, locale = dateLocale(), device = deviceZone(), spans }: TimeOptions = {},
): string | null {
  if (!instant) return null;
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return null;

  // The server's offset for the venue (decision 785): shifted, formatted as UTC.
  const clock = timeZone ? venueClock(timeZone, d, spans) : null;
  if (timeZone && clock) {
    const time = new Intl.DateTimeFormat(locale, {
      timeZone: "UTC",
      hour: "numeric",
      minute: "2-digit",
    }).format(shifted(d, clock.offset));
    if (timeZone === device || clock.offset === browserOffset(device, d)) return time;
    return `${time} ${venueAbbrev(d, timeZone, clock, locale)}`;
  }

  const zone = timeZone ?? device;
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  }).format(d);

  if (!zoneDiffersFromDevice(d, zone, locale, device)) return time;
  return `${time} ${zoneAbbrev(d, zone, locale)}`;
}

/*
 * DATE-ONLY VALUES ARE NOT INSTANTS, and conflating them is the classic
 * off-by-one-day bug in this codebase's shape.
 *
 * `events.event_date` and `tournaments.start_date` are SQL DATEs, sent as
 * "2026-08-29". `new Date("2026-08-29")` parses that as midnight UTC, and
 * formatting midnight UTC in America/Chicago yields the 28th. So every date-only
 * value is formatted in UTC — which is not a fudge: the string has no zone, so
 * reading it in the zone it was written in is the only interpretation that
 * returns the day the organiser typed.
 */

function parseDateOnly(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, d] = m;
  if (!y || !mo || !d) return null;
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
}

/*
 * WHY THE PARTS ARE ASSEMBLED BY HAND INSTEAD OF LETTING Intl FORMAT THE WHOLE
 * DATE, which is otherwise the rule in this file.
 *
 * The design document's examples are consistently weekday-day-month: "Sat 29 –
 * Sun 30 Aug", "Sat 29 · 9:00 AM CDT". `Intl.DateTimeFormat("en", …)` resolves to
 * en-US conventions and produces "Sat, Aug 29" — month first, with a comma. The
 * document is the source of truth for what English looks like, so the order is
 * not Intl's.
 *
 * What is Intl's job, and must stay that way: the weekday NAME, the month NAME,
 * and the digits, in the reader's language (dates follow the language like every
 * other word -- Kevin: "why would part of the text translate but not others?").
 *
 * THE ORDER IS THE CATALOGUE'S: each language declares `format.date.full`,
 * `format.date.dayOnly` and `format.date.range` as patterns over those parts, so
 * English keeps the document's "{{weekday}} {{day}} {{month}}" and Chinese reads
 * "{{month}}{{day}}日{{weekday}}", and the translator owns the arrangement as they
 * own the words. The patterns reach this file through `setDateLocale` rather
 * than through i18next, because the service worker formats times too and does
 * not bundle i18next (decision 488).
 */

/** A language's date patterns (`format.date.*` in its catalogue). */
export interface DatePatterns {
  /** A date with its month: "{{weekday}} {{day}} {{month}}". */
  full: string;
  /** A date under a heading that already named the month. */
  dayOnly: string;
  /** Two dates as a span: "{{from}} – {{to}}". */
  range: string;
}

/** English's patterns, the document's order: the fallback for any key a catalogue lacks. */
export const ENGLISH_DATE_PATTERNS: DatePatterns = {
  full: "{{weekday}} {{day}} {{month}}",
  dayOnly: "{{weekday}} {{day}}",
  range: "{{from}} – {{to}}",
};

let active: { locale: string; patterns: DatePatterns } = {
  locale: "en",
  patterns: ENGLISH_DATE_PATTERNS,
};

/** What a catalogue says about formatting: its date patterns and its Intl locale. */
export interface CatalogueFormat {
  patterns: Partial<DatePatterns>;
  /**
   * The locale Intl formats this language in, where it is not the catalogue's
   * code: Serbian's catalogue is written in Latin script and Intl's `sr` is
   * Cyrillic, so it declares `sr-Latn` (`format.locale`).
   */
  intlLocale?: string;
}

/**
 * The language every formatter here defaults to, and its date patterns: set by
 * the i18n layer when a language activates, and by the service worker for the
 * push it is rendering.
 */
export function setDateLocale(code: string, format: CatalogueFormat = { patterns: {} }): void {
  active = {
    locale: format.intlLocale ?? code,
    patterns: { ...ENGLISH_DATE_PATTERNS, ...format.patterns },
  };
}

/** The Intl locale the formatters here default to. */
export function dateLocale(): string {
  return active.locale;
}

/** A catalogue's `format` object: its date patterns and Intl locale, where it has them. */
export function catalogueFormatOf(catalogue: unknown): CatalogueFormat {
  const format = (catalogue as { format?: { locale?: unknown; date?: Record<string, unknown> } } | null)
    ?.format;
  const patterns: Partial<DatePatterns> = {};
  for (const key of ["full", "dayOnly", "range"] as const) {
    const v = format?.date?.[key];
    if (typeof v === "string") patterns[key] = v;
  }
  return typeof format?.locale === "string" ? { patterns, intlLocale: format.locale } : { patterns };
}

function fill(pattern: string, values: Record<string, string>): string {
  return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name: string) => values[name] ?? m);
}

/** The patterns for `locale`: the active language's, else English's. */
function patternsFor(locale: string): DatePatterns {
  return locale === active.locale ? active.patterns : ENGLISH_DATE_PATTERNS;
}
function dateParts(
  d: Date,
  locale: string,
): { weekday: string; day: string; month: string } {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).formatToParts(d);

  const find = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  return { weekday: find("weekday"), day: find("day"), month: find("month") };
}

function joinDate(
  d: Date,
  locale: string,
  withMonth: boolean,
): string {
  // The weekday's trailing punctuation varies ("Sat" vs "sáb."); nothing is
  // stripped, because removing a locale's own punctuation is the kind of tidying
  // that reads as broken to a native speaker.
  const p = patternsFor(locale);
  return fill(withMonth ? p.full : p.dayOnly, dateParts(d, locale));
}

/**
 * A date range: "Sat 29 – Sun 30 Aug", or "Sun 30 Aug – Tue 1 Sep" across a month
 * boundary, or "Sat 29 Aug" for a single day.
 *
 * The month is omitted from the first date only when both fall in the same month,
 * per build rule 7's "month added across month spans". The saving is small and the
 * rule is about scanning: a weekend's two dates read as one span when the month
 * appears once.
 */
export function dateRange(
  start: string | null | undefined,
  end: string | null | undefined,
  locale = dateLocale(),
): string | null {
  const from = start ? parseDateOnly(start) : null;
  if (!from) return null;
  const to = end ? parseDateOnly(end) : null;

  const fmt = (d: Date, withMonth: boolean) => joinDate(d, locale, withMonth);

  if (!to || to.getTime() === from.getTime()) return fmt(from, true);

  const sameMonth =
    from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth();

  // English's is an en dash with spaces, the typographic convention for a range;
  // each language's own is in its catalogue.
  return fill(patternsFor(locale).range, { from: fmt(from, !sameMonth), to: fmt(to, true) });
}

/**
 * An INSTANT rendered as a venue-local day: "Sat 29 Aug".
 *
 * DISTINCT FROM dateLabel, AND THE DISTINCTION IS A BUG THIS FIXES. `dateLabel`
 * takes a date-only value ("2026-08-29"), which has no zone and is formatted in
 * UTC so the day never shifts. This takes a timestamptz and must be formatted in
 * the VENUE zone, because the whole question is which day it is there.
 *
 * The bug was slicing the day out of the ISO string — `closes.slice(0, 10)` — and
 * formatting the time in the venue zone, so the two disagreed whenever the UTC
 * date and the venue date differed. A registration deadline at 02:59Z in Denver
 * rendered "Tue 1 Sep, 8:59 PM" when the truth was Mon 31 Aug: a whole day wrong,
 * on the one value in the app where being wrong costs somebody an entry.
 *
 * This is exactly what invariant 6 exists to prevent — "never store naive local
 * times" has a client-side twin, which is never to derive a local date by string
 * surgery on a UTC instant.
 */
export function venueDateLabel(
  instant: string | null | undefined,
  timeZone: string | null | undefined,
  locale = dateLocale(),
): string | null {
  if (!instant) return null;
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return null;

  // The server's offset where it has one (decision 785).
  const clock = timeZone ? venueClock(timeZone, d) : null;
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: clock ? "UTC" : (timeZone ?? deviceZone()),
    weekday: "short",
    day: "numeric",
    month: "short",
  }).formatToParts(clock ? shifted(d, clock.offset) : d);

  const find = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  // Assembled in the language's own order, like dateRange — see the note there.
  return fill(patternsFor(locale).full, {
    weekday: find("weekday"),
    day: find("day"),
    month: find("month"),
  });
}

/**
 * A single date-only value: "Sat 29 Aug", or "Sat 29" with `withMonth` false.
 *
 * BUILD RULE 7 IS "weekday+day, MONTH ADDED ACROSS MONTH SPANS", and the month
 * is a caller's decision because only the caller knows what group the date is
 * being read inside. `dateRange` already applies the rule to its own two dates;
 * this is the same rule for a date rendered under a heading that has already
 * named the month.
 *
 * Defaulted to true, so every existing caller keeps the reading it had and the
 * only place that opts out is the one that measured its own span.
 */
export function dateLabel(
  value: string | null | undefined,
  locale = dateLocale(),
  withMonth = true,
): string | null {
  if (!value) return null;
  const d = parseDateOnly(value);
  if (!d) return null;
  return joinDate(d, locale, withMonth);
}

/**
 * The venue's calendar day as "YYYY-MM-DD", for comparing against a SQL DATE.
 *
 * en-CA because its date format IS ISO, so the result compares
 * lexicographically with the wire's date-only strings without parsing either. A
 * null zone falls back to the device zone, which is all we have — the same
 * reading venueTime takes.
 *
 * The same function exists in activeState.ts for the done-cascade's day
 * backstop. Kept separate rather than shared because that one is a rule about
 * WHEN A CARD RETIRES and this one is a formatting input; folding them together
 * would put a display concern inside the visibility math, where a later change
 * to either would silently move the other.
 *
 * Exported for Upcoming's split-event line, which names the day of its
 * earliest tier's INSTANT rather than of a date column.
 */
export function venueDay(now: Date, timeZone: string | null | undefined): string {
  // The server's offset where it has one (decision 785).
  const clock = timeZone ? venueClock(timeZone, now) : null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: clock ? "UTC" : (timeZone ?? undefined),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(clock ? shifted(now, clock.offset) : now);
}

/**
 * What a date-only value should be CALLED, relative to the venue's today.
 *
 * A DISCRIMINATOR AND NOT A STRING, which is the whole reason this returns a
 * union. "Today" and "Tomorrow" are copy, and copy lives in the translation
 * file (§7's no-literals rule); this file states as its own rule that month and
 * weekday names never appear here. So the caller translates the two words and
 * renders `label` for everything else — which is also what lets a locale that
 * has no single word for "the day after today" fall back gracefully.
 *
 * WHY RELATIVE AT ALL, reversing deviations #27's "Not Today / Tomorrow". That
 * entry's objection was that a relative word GOES STALE — a phone left open
 * overnight at a venue would show Sunday's events under "Tomorrow" on Sunday
 * morning — and its conclusion was that a date cannot be wrong. Two things
 * changed. The wake-up refresh (decision 384) re-asks on `visibilitychange`, so
 * the pocketed phone is exactly the case that now refreshes before it is read.
 * And `now` is a parameter here, threaded from the same clock every other
 * relative label on the screen uses, so this cannot drift independently of
 * them: if "as of 4 min ago" is current, so is this.
 *
 * WHICH DAY IS "TODAY" DEPENDS ON THE SCREEN. Upcoming compares against the
 * VENUE's day, through `dayName` (decision 475, build rule 7): a coach in Denver
 * reading about an Eastern tournament at 10pm is looking at a venue where it is
 * already tomorrow, and labelling the venue's Saturday "Tomorrow" because the
 * reader's clock says Friday would be the same class of error decision 316
 * exists to prevent, one level up. Active compares against the READER's day,
 * through `readerDayName` (decision 787): it is read by someone watching along
 * from wherever they are, and their "Tomorrow" is their own. The mismatch that
 * can cause -- a Saturday venue morning that is the reader's Friday night -- is
 * answered by the weekday `activeTime` adds across a date boundary (788), not by
 * tying the marker to the times.
 */
export type DayName =
  | { kind: "today" }
  | { kind: "tomorrow" }
  | { kind: "date"; label: string };

export function dayName(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  locale = dateLocale(),
  withMonth = true,
  now: Date = new Date(),
): DayName | null {
  return nameAgainst(value, venueDay(now, timeZone), locale, withMonth);
}

/**
 * The same naming as `dayName`, against the DEVICE's calendar day at `now`
 * (decision 787). Active only; see the comment on `DayName` for why the two
 * screens differ. A sibling rather than a flag, so each call site's meaning is
 * visible and `dayName`'s positional callers are untouched.
 *
 * `device` is injectable for tests, as it is for `venueTime`.
 */
export function readerDayName(
  value: string | null | undefined,
  locale = dateLocale(),
  withMonth = true,
  now: Date = new Date(),
  device = deviceZone(),
): DayName | null {
  return nameAgainst(value, deviceDay(now, device), locale, withMonth);
}

/** A zone's calendar day as "YYYY-MM-DD", read through Intl's own en-CA ISO form. */
function deviceDay(instant: Date, device: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: device,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

/** Name `value` against a given `today`, a bare "YYYY-MM-DD" in whichever zone the caller chose. */
function nameAgainst(
  value: string | null | undefined,
  today: string,
  locale: string,
  withMonth: boolean,
): DayName | null {
  const label = dateLabel(value, locale, withMonth);
  if (!label || !value) return null;

  if (value === today) return { kind: "today" };

  // Tomorrow, by incrementing the calendar day. UTC arithmetic on a date-only
  // string is safe HERE where it is not elsewhere: `today` is already a bare
  // calendar date, and adding 24 hours to midnight UTC of a bare date cannot
  // cross a DST boundary because there is no zone in it. Deriving it by adding
  // 24 hours to `now` INSTEAD would be wrong, and is the trap: a spring-forward
  // day is 23 hours long.
  const base = parseDateOnly(today);
  if (!base) return { kind: "date", label };
  const next = new Date(base.getTime() + 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  if (value === next) return { kind: "tomorrow" };

  return { kind: "date", label };
}

/**
 * How far apart the venue and the device must be, in minutes, before Active adds
 * the reader's own clock to a venue time (decision 788). EXACTLY this gap does
 * not trigger: Pacific reading an Eastern event is 180 minutes and sees
 * "9:00 AM EDT" alone, because within three hours a reader can convert in their
 * head.
 */
export const READER_TIME_THRESHOLD_MINUTES = 180;

export interface ActiveTimeParts {
  /** Exactly what `venueTime` returns: "8:00 AM GMT+1". */
  venue: string;
  /** The device's clock for the same instant, "11:00 PM" or "Fri 11:00 PM"; null when the zones are near. */
  reader: string | null;
}

/**
 * A venue time for the Active screen, with the reader's own clock beside it when
 * the venue is far from the device (decision 788). Parts rather than one string,
 * so the caller can wrap the catalogue's parenthetical around `reader` and keep
 * each part from breaking internally.
 *
 * The trigger is the gap between the two offsets AT THAT INSTANT, so a pair that
 * is 180 minutes apart in summer and 240 in winter (Puerto Rico seen from the
 * Pacific) triggers only in winter. The venue's offset is the server's where it
 * has one (785); the device's is the browser's. If either is unknown, or the
 * tournament has no zone, there is nothing to compare and no reader part.
 *
 * The weekday is added only when the device's calendar date differs from the
 * venue's at the instant, and its position is whatever the locale's formatter
 * puts it in.
 */
export function activeTime(
  instant: string | null | undefined,
  { timeZone, locale = dateLocale(), device = deviceZone(), spans }: TimeOptions = {},
): ActiveTimeParts | null {
  const venue = venueTime(instant, { timeZone, locale, device, spans });
  if (venue == null) return null;
  if (!instant || !timeZone) return { venue, reader: null };

  const d = new Date(instant);
  const venueOffset = venueClock(timeZone, d, spans)?.offset ?? browserOffset(timeZone, d);
  const deviceOffset = browserOffset(device, d);
  if (venueOffset == null || deviceOffset == null) return { venue, reader: null };
  if (Math.abs(venueOffset - deviceOffset) <= READER_TIME_THRESHOLD_MINUTES) return { venue, reader: null };

  const acrossDates = deviceDay(d, device) !== venueDay(d, timeZone);
  const reader = new Intl.DateTimeFormat(locale, {
    timeZone: device,
    ...(acrossDates ? { weekday: "short" as const } : {}),
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return { venue, reader };
}

/**
 * The DAY a registration deadline means, for the day-only deadline line
 * (decision 360, roadmap item 19: "stop trying to be clever, just report the
 * day").
 *
 * The one piece of cleverness that stays, because it is what makes the plain
 * day HONEST: every stored tournament deadline is 23:59:59 in SOME US zone by
 * construction — FRED's is end-of-day in the venue zone (decision 134), USA
 * Fencing states its own as 23:59:59 PACIFIC platform-wide (decision 144) — so
 * an Eastern tournament's USA Fencing deadline is an instant at 02:59:59 the
 * NEXT venue-local day. Printing that date hands the reader a day they mostly
 * do not have.
 *
 * Subtracting 12 hours lands mid-day of the intended day for ANY pairing of US
 * zones (stated zone offsets UTC-4..-10, venue likewise: the shifted instant
 * renders between 05:59 and 17:59 venue-local of the stated day), so the label
 * is the day the source meant in both regimes — venue-zone FRED and
 * Pacific-fixed USA Fencing — without storing per-field provenance, which
 * remains deferred (decision 180).
 */
export function deadlineDayLabel(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  locale = dateLocale(),
): string | null {
  if (!value) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  const shifted = new Date(instant.getTime() - 12 * 60 * 60 * 1000);
  return venueDateLabel(shifted.toISOString(), timeZone, locale);
}

/**
 * A fencer's name for DISPLAY: "Dobrosavljević, Kalina" → "Kalina
 * Dobrosavljević", and "KOWALCZYK Piotr" → "Piotr Kowalczyk".
 *
 * Display only, never a sort key (decision 358). The corpus stores names
 * surname-first and the fencer axis sorts on that stored string, which is what
 * keeps the bands in surname order after this flips what they show.
 *
 * REFORMAT ONLY WHAT PARSES CONFIDENTLY, and there are now TWO shapes that do.
 * Confidence stays structural — this never consults a dictionary of names and
 * never guesses from resolution status:
 *
 *   "Last, First"     exactly one comma. The corpus's own stored form.
 *   "SURNAME Given"   a leading run of ALL-CAPS tokens followed by at least one
 *                     that is not. FTL's house style, and the only spelling we
 *                     will ever have for some people.
 *
 * WHY THE SECOND ONE WAS ADDED (decision 469's sibling; this amends 358).
 * 358 declined it, on the reasoning that guessing at a raw source spelling
 * mangles exactly the rows the unconfirmed marker exists to be honest about.
 * That argument holds for a spelling we cannot parse and does not hold for this
 * one, which is not a guess: "SURNAME Given" is a STRUCTURE, as recognisable as
 * a comma, and FTL prints it for every competitor it names. What tipped it is
 * where the shouted names actually appear — on the DE opponent, who is by
 * construction somebody nobody tracks (decision 457), so there will never be a
 * canonical spelling to prefer instead. Leaving it alone did not preserve a
 * better rendering; it just left one name on the screen in a different format
 * from every other.
 *
 * THE RECASE IS PART OF IT, and reordering alone was considered and rejected:
 * "Piotr KOWALCZYK" fixes the order and keeps the shouting, which is still one
 * row that does not match its neighbours.
 *
 * WHAT THE RECASE CANNOT DO, stated rather than discovered: it cannot restore
 * capitalisation INSIDE a surname. "MCDONALD" becomes "Mcdonald", not
 * "McDonald", because no rule separates that from "MACIEL" — and special-casing
 * the prefix would produce "MacIel". Apostrophes and hyphens ARE handled, since
 * they are punctuation rather than a naming convention: "O'BRIEN" → "O'Brien",
 * "PIERRE-LOUIS" → "Pierre-Louis". The residual error is cosmetic and strictly
 * smaller than what it replaces, which rendered the whole surname shouted.
 *
 * FOUR REFUSALS, each closing a way this would state something false:
 *
 *   - EVERY token all-caps ("JOHN SMITH"). Nothing says which end the surname
 *     is, so flipping would be a coin toss. Untouched.
 *   - No leading all-caps token ("Jo SMITH", "Smith Jo"). Not this shape.
 *   - One token. There is nothing to reorder.
 *   - A token with no CASED letters, which is what keeps this away from the
 *     uncased scripts. "李 明" and Arabic names read as all-caps to a naive
 *     `s === s.toUpperCase()` — every uncased character equals its own
 *     uppercase — so the test additionally requires a letter whose upper and
 *     lower forms differ. Arabic is tier 1 (§7), so this is a live case rather
 *     than a hypothetical.
 *
 * Deliberately NOT touched, exactly as 358 has it: the Admin alias queue,
 * ManageFencers' roster and the fencer search. Those are identity-management
 * surfaces where the stored form is the fact being managed, and none of them
 * calls this function.
 */
export function firstLast(name: string): string {
  const parts = nameParts(name);
  return parts ? `${parts.given} ${parts.surname}` : name;
}

/**
 * The two halves of a name, or null where neither shape parses.
 *
 * FACTORED OUT SO THERE IS ONE PARSE AND NOT TWO. `firstLast` and
 * `notificationName` answer different questions about the same structure, and
 * a second implementation of "which end is the surname" is the shape that
 * drifts: the four refusals, the recase and the particle-keeping caps run would
 * all have to be maintained twice. Null is the refusal, and it means exactly
 * what `firstLast` returning its input unchanged has always meant.
 */
interface NameParts {
  given: string;
  surname: string;
}

function nameParts(name: string): NameParts | null {
  const comma = name.indexOf(",");
  if (comma >= 0) {
    // Two commas decline: a suffix is not confidently separable.
    if (name.indexOf(",", comma + 1) >= 0) return null;
    const surname = name.slice(0, comma).trim();
    const given = name.slice(comma + 1).trim();
    if (!surname || !given) return null;
    return { given, surname };
  }
  return capsSurnameParts(name);
}

/** The three formats a notification can name a tracked fencer in. */
export type NotificationNameFormat = "first" | "last" | "full";

/**
 * A tracked fencer's name as NOTIFICATION COPY names them (decision 535).
 *
 * The reader's own account chooses between the given name, the surname and the
 * whole name, account-wide across every notification kind and every list. It is
 * the same structural parse `firstLast` uses and NOT a second, looser one:
 * "full" returns exactly what `firstLast` returns.
 *
 * A NAME THAT DOES NOT PARSE RETURNS WHOLE, FOR EVERY FORMAT, which is the
 * boundary against decision 358 rather than a convenience. `firstLast`'s four
 * refusals each close a way this would state something false -- "JOHN SMITH"
 * does not say which end the surname is, and a single token has nothing to
 * split -- and a preference for "just the first name" cannot license a guess
 * those refusals exist to refuse. So the reader gets the stored spelling, which
 * is a name rather than the wrong half of one (Kevin, asked directly: "Whole
 * string is fine").
 *
 * THIS IS LOSSY FOR NON-WESTERN NAMES AND THAT IS ACCEPTED RATHER THAN
 * UNNOTICED (Kevin: "Yes, this could get lossy with non-Western names. That is
 * acceptable for now"). "Given" and "surname" are the two slots the parse has,
 * and the naming orders this corpus stores -- FRED's "Last, First" and FTL's
 * "SURNAME Given" -- are what it recognises. A name whose structure is neither
 * falls to the refusal above and is printed whole, which is the honest failure:
 * the lossiness is in calling one half "the first name", never in silently
 * dropping the other.
 *
 * THE OPPONENT IS NOT COVERED BY THIS and always renders through plain
 * `firstLast` (Kevin: "A user would be familiar with their own fencers but
 * likely less so with opponents at a large event. 'John on strip 7 vs John' --
 * the first part always makes sense, the second part almost never does").
 */
export function notificationName(name: string, format: NotificationNameFormat): string {
  const parts = nameParts(name);
  if (!parts) return name;
  switch (format) {
    case "first":
      return parts.given;
    case "last":
      return parts.surname;
    default:
      return `${parts.given} ${parts.surname}`;
  }
}

/** Whether a token carries at least one letter that HAS a case. */
function hasCasedLetter(token: string): boolean {
  for (const ch of token) {
    if (ch.toLowerCase() !== ch.toUpperCase()) return true;
  }
  return false;
}

/** Whether a token is a shouted word: cased, and equal to its own uppercase. */
function isShouted(token: string): boolean {
  return hasCasedLetter(token) && token === token.toLocaleUpperCase();
}

/**
 * Title-case a shouted token, per run of letters, so punctuation inside a
 * surname keeps the letter after it capitalised.
 *
 * The first letter of each run is already uppercase (the token is shouted), so
 * this only lowercases what follows it. Combining marks travel with the letter
 * they modify, or a decomposed "Ć" would end a run early and capitalise the
 * next letter.
 */
function titleCase(token: string): string {
  return token.replace(
    /(\p{L})([\p{L}\p{M}]*)/gu,
    (_, first: string, rest: string) => first + rest.toLocaleLowerCase(),
  );
}

function capsSurnameParts(name: string): NameParts | null {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;

  // The leading run of shouted tokens is the surname: "VAN DER BERG Jan" has a
  // three-token one, and taking the whole run is what keeps a particle with the
  // name it belongs to.
  let surnameEnd = 0;
  while (surnameEnd < tokens.length && isShouted(tokens[surnameEnd]!)) surnameEnd++;

  // No run, or the run swallowed everything — the all-caps refusal.
  if (surnameEnd === 0 || surnameEnd === tokens.length) return null;

  // The remainder has to look like a given name rather than more shouting that
  // happens to follow a cased token.
  const given = tokens.slice(surnameEnd);
  if (!given.some(hasCasedLetter)) return null;

  return {
    given: given.join(" "),
    surname: tokens.slice(0, surnameEnd).map(titleCase).join(" "),
  };
}

/*
 * ORDINALS ("3rd", "14th") ARE DELIBERATELY NOT IN THIS FILE.
 *
 * They belong to i18next, via `t(key, { count, ordinal: true })`, which selects
 * from the locale's own ordinal plural categories and picks the matching
 * `_ordinal_one` / `_ordinal_two` / `_ordinal_few` / `_ordinal_other` string. That
 * is the same Intl.PluralRules data a helper here would consult, reached through
 * the layer that also holds the words.
 *
 * A helper was written here first and then removed, which is worth recording:
 * having both meant a caller could format an ordinal WITHOUT going through the
 * translation file, producing "3rd" in Russian. One mechanism is the point.
 */
